import type { ComponentType } from 'react';

const CHUNK_RETRY_DELAYS_MS = [250, 1000];
const CHUNK_RELOAD_THROTTLE_MS = 30_000;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk \d+ failed|ChunkLoadError/i.test(message);
}

function wait(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function readRetryAt(key: string) {
  try {
    return Number(window.sessionStorage.getItem(key) || 0);
  } catch {
    return 0;
  }
}

function writeRetryAt(key: string) {
  try {
    window.sessionStorage.setItem(key, String(Date.now()));
  } catch {
    // If sessionStorage is unavailable, fall back to surfacing the original error.
  }
}

function clearRetryAt(key: string) {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Ignore storage errors. The retry marker is only a loop guard.
  }
}

export function requestChunkReloadOnce(key = 'ef_chunk_retry_at', throttleMs = CHUNK_RELOAD_THROTTLE_MS): boolean {
  const lastRetryAt = readRetryAt(key);
  const retryExpired = !lastRetryAt || Date.now() - lastRetryAt > throttleMs;
  if (!retryExpired) return false;

  writeRetryAt(key);
  window.location.reload();
  return true;
}

export async function lazyRetry<T extends { default: ComponentType<any> }>(
  factory: () => Promise<T>,
  key = 'ef_chunk_retry_at',
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= CHUNK_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      const module = await factory();
      clearRetryAt(key);
      return module;
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;
      lastError = error;

      const delay = CHUNK_RETRY_DELAYS_MS[attempt];
      if (delay !== undefined) {
        await wait(delay);
      }
    }
  }

  if (requestChunkReloadOnce(key)) {
    return new Promise<T>(() => undefined);
  }

  throw lastError;
}
